import { useSyncExternalStore } from 'react';
import { DEFAULT_PROJECT_SORT_BY } from '../resources/constants';
import type { ProjectSortBy } from '../resources/projects/types';
import { appComponentsStorage } from '../storage';

const STORAGE_KEY = 'frame-io--project-sort-by';

/**
 * Creates a memoized snapshot getter for project sorting preferences.
 * This is necessary because useSyncExternalStore requires consistent object references
 * to prevent infinite re-renders.
 */
function createGetSnapshot() {
  let cachedValue: ProjectSortBy | null = null;

  return () => {
    const currentValue =
      appComponentsStorage.get(STORAGE_KEY) || DEFAULT_PROJECT_SORT_BY;

    // Only update cache if sorting preferences have changed
    if (
      !cachedValue ||
      cachedValue.field !== currentValue.field ||
      cachedValue.order !== currentValue.order
    ) {
      cachedValue = currentValue;
    }

    return cachedValue;
  };
}

const store = {
  subscribe: (callback: () => void) => {
    window.addEventListener('storage', callback);
    return () => window.removeEventListener('storage', callback);
  },

  // Create a single, shared instance of the snapshot getter
  snapshot: createGetSnapshot(),
};

export function useProjectSortBy() {
  const sortBy = useSyncExternalStore(
    store.subscribe,
    store.snapshot,
    () => DEFAULT_PROJECT_SORT_BY // Server snapshot
  );

  const updateSortBy = (newSortBy: ProjectSortBy) => {
    appComponentsStorage.set(STORAGE_KEY, newSortBy);

    // Workaround for a browser limitation:
    // the 'storage' event is only triggered in other tabs or windows,
    // not in the same tab that modified the storage.
    // Manually dispatch the event to notify all consumers.
    window.dispatchEvent(new StorageEvent('storage', { key: STORAGE_KEY }));
  };

  return [sortBy, updateSortBy] as const;
}
